import { setUserId, identify, Identify } from '@amplitude/analytics-browser'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Power, ChevronRight, MessageSquare, LifeBuoy } from '@geist-ui/react-icons'
import { animated, useSpring, config } from 'react-spring'
import { getShortenedAddress } from '../../utils/address'
import React from 'react'
import { useRouter } from 'next/router'
import { fbEnrichUsersFromIds } from '../../data/FirebaseEnrich'
import { FbEnrichedUser } from '../../interfaces/User'
import ProfileImage from '../profile/image'
import useAuth from '../../hooks/auth'
import { useQuery } from 'react-query'
import { Group, Whatshot } from '@material-ui/icons'
import log from '../../events/log'
import {
  CloseAccountDropdownEvent,
  DisconnectWalletEvent,
  OpenAccountDropdownEvent,
} from '../../events/account_events'
import { useMediaQuery } from '@material-ui/core'

// =========================== React component  ================================

const Profile = (): JSX.Element | null => {
  const [user, setUser] = useState<FbEnrichedUser | undefined>(undefined)
  const [profileHover, setProfileHover] = useState(false)
  const [visible, setVisible] = useState(false)
  const [opened, setOpened] = useState(false)
  const router = useRouter()
  const [balance, setBalance] = useState('0')
  const { profile, disconnect, fetchBalance } = useAuth()
  const isTablet = useMediaQuery('(max-width: 62em)')

  const profileHoverState = useSpring({
    boxShadow: profileHover ? '0px 5px 25px rgba(255,255,255,0.2)' : '0px 0px 0px rgba(0,0,0,0)',
    transform: profileHover ? `translate3d(0,-5%,0)` : `translate3d(0,0%,0)`,
    config: {
      mass: 1,
      tension: 170,
      friction: 26,
      velocity: 0,
    },
  })

  const menuOpenState = useSpring({
    opacity: visible ? 1 : 0,
    // transform: visible ? `translate3d(0,0%,0)` : `translate3d(0%,-10%,0)`,
    config: config.gentle,
    onRest: () => {
      setOpened(visible)
    },
  })

  const toggleHide = (): void => {
    if (opened) {
      setVisible(false)
      const element = document.getElementById('profile-menu')
      element?.blur()

      log(new CloseAccountDropdownEvent())
    } else {
      setVisible(true)

      log(new OpenAccountDropdownEvent())
    }
  }

  const getEnrichedUser = async (): Promise<void> => {
    if (profile) {
      const users = await fbEnrichUsersFromIds([profile.address])
      if (users.length > 0) {
        setUser(users[0])
      }
    }
  }

  //TODO: Revise the set user id here.
  useEffect(() => {
    if (profile) {
      setUserId(profile.address)
      const id = new Identify()
      id.set('balance', balance)
      identify(id)
      getEnrichedUser()
    }
  }, [profile, balance])

  const updateBalance = async (): Promise<void> => {
    if (profile) {
      const balance = await fetchBalance()
      setBalance((balance / 1000000000).toString())
    }
  }

  useQuery('balance', updateBalance, {
    refetchInterval: visible ? 1000 : false,
  })

  useEffect(() => {
    if (visible) {
      updateBalance()
    }
  }, [visible])

  if (!profile) {
    return null
  }

  const openProfile = (): void => {
    if (profile) {
      router.push(`/${profile.address}`)
      toggleHide()
    }
  }

  return (
    <OuterDiv id="profile-menu" tabIndex={0} onBlur={() => setVisible(false)}>
      <ProfileDiv
        style={profileHoverState}
        onMouseEnter={() => setProfileHover(true)}
        onMouseLeave={() => setProfileHover(false)}
        onClick={toggleHide}
      >
        <ProfileImage item={user} size={30} />
      </ProfileDiv>
      <Menu visible={visible} style={menuOpenState}>
        <PopoverWrapper>
          <MenuItem onClick={openProfile}>
            <MenuItemLeft>
              <ProfileImage item={user} size={35} />
              <MenuItemTitle color="white">{user?.displayName ?? 'View Profile'}</MenuItemTitle>
            </MenuItemLeft>
            <ChevronRight />
          </MenuItem>
          <BalanceDiv
            onClick={() =>
              window.open(
                `https://explorer.solana.com/address/${profile.address}${
                  process.env.NEXT_PUBLIC_ENVIRONMENT === 'testing' ? '?cluster=devnet' : ''
                }`,
                '_blank'
              )
            }
          >
            <BalanceTitleDiv>
              <BalanceTitle>Wallet Balance</BalanceTitle>
              <PopoverAddress>{getShortenedAddress(profile.address)}</PopoverAddress>
            </BalanceTitleDiv>
            <Balance>{`${Number(balance).toFixed(3)} SOL`}</Balance>
          </BalanceDiv>
          {isTablet && (
            <MenuItem onClick={() => router.push('/trending')}>
              <MenuItemLeft>
                <Whatshot />
                <MenuItemTitle color="white">Trending</MenuItemTitle>
              </MenuItemLeft>
              <ChevronRight />
            </MenuItem>
          )}
          <MenuItem onClick={() => window.open('http://discord.gg/FdBj6ZBuuJ', '_blank')}>
            <MenuItemLeft>
              <Group />
              <MenuItemTitle color="white">Discord</MenuItemTitle>
            </MenuItemLeft>
            <ChevronRight />
          </MenuItem>
          <MenuItem onClick={() => window.open('https://glass.helpkit.so/', '_blank')}>
            <MenuItemLeft>
              <LifeBuoy />
              <MenuItemTitle color="white">Help</MenuItemTitle>
            </MenuItemLeft>
            <ChevronRight />
          </MenuItem>
          <MenuItem onClick={() => window.open('https://glass.canny.io/', '_blank')}>
            <MenuItemLeft>
              <MessageSquare />
              <MenuItemTitle color="white">Feedback</MenuItemTitle>
            </MenuItemLeft>
            <ChevronRight />
          </MenuItem>
          <MenuItem
            onClick={() => {
              log(new DisconnectWalletEvent())

              disconnect()
            }}
          >
            <MenuItemLeft>
              <Power color="#cdcdcd" />
              <MenuItemTitle color="#cdcdcd">Disconnect</MenuItemTitle>
            </MenuItemLeft>
            <ChevronRight color={'#cdcdcd'} />
          </MenuItem>
        </PopoverWrapper>
      </Menu>
    </OuterDiv>
  )
}

export default Profile

// ========================== Styled components ================================

const OuterDiv = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;

  margin-right: 10px;

  @media (min-width: 43em) {
    margin-right: 25px;
  }
`

const ProfileDiv = styled(animated.div)`
  display: flex;
  align-items: center;
  flex-direction: row;
  border-radius: 50%;
  background: #101010;
  border-width: 1px;
  border-color: #aaa;
  border-style: solid;
  padding: 6px;
  cursor: pointer;
`

const BalanceDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: calc(100% - 30px);
  padding: 15px;
  cursor: pointer;
  border-radius: 10px;
  border-width: 1px;
  border-color: #aaa;
  border-style: solid;
  margin: 10px 0;
`

const BalanceTitleDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  align-items: center;
`

const BalanceTitle = styled.div`
  font-weight: 700;
  color: #cdcdcd;
  font-size: 14px;
`

const Balance = styled.div`
  font-weight: 600;
  color: white;
  font-size: 18px;
`

const PopoverAddress = styled.div`
  font-weight: 600;
  color: white;
  font-size: 12px;
  padding: 2px 10px;
  border-radius: 10px;
  background: #333;
`

const PopoverWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 250px;
`

const Menu = styled(animated.div)<{ visible: boolean }>`
  position: absolute;
  z-index: 6;
  top: calc(100% + 5px);
  background: linear-gradient(rgb(35, 35, 35), rgb(20, 20, 20));
  border-width: 1px;
  border-color: #333;
  border-style: solid;
  padding: 10px;
  border-radius: 10px;
  box-shadow: 0px 15px 25px rgba(255, 255, 255, 0.052);
  pointer-events: ${(props) => (props.visible ? 'all' : 'none')};

  right: 10px;

  :focus,
  *:focus {
    outline: none;
  }

  @media (min-width: 43em) {
    right: 25px;
  }
`

const MenuItem = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: calc(100% - 20px);
  padding: 15px 10px;
  align-items: center;
  cursor: pointer;
  border-radius: 10px;

  :hover {
    background: #232323;
  }
`

const MenuItemLeft = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const MenuItemTitle = styled.div<{ color: string }>`
  font-weight: 600;
  color: ${(props) => props.color};
  font-size: 16px;
  margin-left: 15px;
`
